
import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Loader2, Award, Calendar } from 'lucide-react';
import { adminGrantPremium, adminRemovePremium } from '@/lib/actions/adminPremiumActions';
import { useToast } from '@/hooks/toast';

interface AdminPremiumDialogProps {
  isOpen: boolean;
  onClose: () => void;
  listingId: number;
  listingTitle: string;
  adminId: string;
  currentPremiumStatus: boolean;
  isAdminGranted?: boolean;
  onSuccess: () => void;
}

const AdminPremiumDialog: React.FC<AdminPremiumDialogProps> = ({
  isOpen,
  onClose,
  listingId,
  listingTitle,
  adminId,
  currentPremiumStatus,
  isAdminGranted,
  onSuccess
}) => {
  const [adminNotes, setAdminNotes] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const handleGrantPremium = async () => {
    setIsLoading(true);
    try {
      const success = await adminGrantPremium(listingId, adminId, adminNotes);
      if (success) {
        toast({
          title: "Premium Status Granted",
          description: `Listing "${listingTitle}" has been made premium for 28 days.`,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to grant premium status. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemovePremium = async () => {
    setIsLoading(true);
    try {
      const success = await adminRemovePremium(listingId, adminId);
      if (success) {
        toast({
          title: "Premium Status Removed",
          description: `Premium status removed from "${listingTitle}".`,
        });
        onSuccess();
        onClose();
      } else {
        toast({
          title: "Error",
          description: "Failed to remove premium status. Please try again.",
          variant: "destructive",
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const expirationDate = new Date();
  expirationDate.setDate(expirationDate.getDate() + 28);

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Award className="h-5 w-5 text-amber-500" />
            {currentPremiumStatus ? 'Remove Premium Status' : 'Grant Premium Status'}
          </DialogTitle>
          <DialogDescription>
            {currentPremiumStatus 
              ? `Remove premium status from "${listingTitle}"`
              : `Grant premium status to "${listingTitle}" as an administrator.`
            }
          </DialogDescription>
        </DialogHeader>

        {!currentPremiumStatus && (
          <div className="space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <div className="flex items-center gap-2 text-amber-700">
                <Calendar className="h-4 w-4" />
                <span className="text-sm font-medium">Premium Duration: 28 days</span>
              </div>
              <p className="text-xs text-amber-600 mt-1">
                Expires on: {expirationDate.toLocaleDateString()}
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="admin-notes">Admin Notes (Optional)</Label>
              <Textarea
                id="admin-notes"
                placeholder="Reason for granting premium status..."
                value={adminNotes}
                onChange={(e) => setAdminNotes(e.target.value)}
                className="min-h-[80px]"
              />
            </div>
          </div>
        )}

        {currentPremiumStatus && isAdminGranted && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3">
            <p className="text-sm text-red-700">
              This listing has admin-granted premium status. Removing it will immediately 
              make the listing non-premium.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isLoading}>
            Cancel
          </Button>
          <Button
            onClick={currentPremiumStatus ? handleRemovePremium : handleGrantPremium}
            disabled={isLoading}
            variant={currentPremiumStatus ? "destructive" : "default"}
          >
            {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {currentPremiumStatus ? 'Remove Premium' : 'Grant Premium'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AdminPremiumDialog;
